import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ContentEditorModal from '../components/ContentEditorModal';
import ShareModal from '../components/ShareModal';
import PromptsEditor from '../components/PromptsEditor';
import { api } from '../services/api';
import ModelSelector from '../components/ModelSelector';

// ── Types ──────────────────────────────────────────────────────────────────

interface MapOption { id: number; name: string; total_topics: number; total_clusters: number; }
interface TopicOption { id: number; topic_name: string; primary_keyword: string; cluster_name: string; tier: string; search_volume: number; }
interface CptOption { slug: string; rest_base: string; name: string; }

interface ArticleSummary {
  id: number;
  keyword: string;
  title: string | null;
  h1: string | null;
  slug: string | null;
  word_count: number;
  status: string;
  is_edited: boolean;
  wp_url: string | null;
  created_at: string | null;
  error_message: string | null;
  pipeline_logs: string[] | null;
  prompt_mode: string;  // 'holly' or 'standard'
}

interface ArticleDetail extends ArticleSummary {
  project_id: number;
  supporting_keywords: string[] | null;
  meta_description: string | null;
  meta_title: string | null;
  content_html: string | null;
  research_data: any;
  outline_data: any;
  article_metadata: any;
  edited_at: string | null;
  edited_by_name: string | null;
  wp_post_id: number | null;
  wp_scheduled_date: string | null;
  wp_status: string | null;
  publish_mode: string | null;
  published_at: string | null;
  edit_history: Array<{ action: string; user?: string; timestamp: string; details?: string }> | null;
  // Image fields
  images_status: string | null;
  thumbnail_url: string | null;
  thumbnail_prompt: string | null;
  thumbnail_title: string | null;
  infographic_urls: Array<{ url: string; base64_uri: string; prompt: string; after_heading?: string; description: string; filename: string }> | null;
}

interface WpAuthor { id: number; name: string; slug: string; }

// ── Spinner ────────────────────────────────────────────────────────────────
const Spinner = () => (
  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// ── Pipeline Stage Tracker ─────────────────────────────────────────────────

const PIPELINE_STAGES = [
  { key: 'input', label: 'Start', icon: '1' },
  { key: 'research', label: 'Research', icon: '2' },
  { key: 'outline', label: 'Outline', icon: '3' },
  { key: 'write', label: 'Write', icon: '4' },
  { key: 'verify', label: 'Verify', icon: '5' },
  { key: 'complete', label: 'Done', icon: '6' },
];

function detectStage(article: ArticleSummary): string {
  if (article.status === 'completed' || article.status === 'published') return 'complete';
  if (article.status === 'failed') return 'failed';
  if (article.status !== 'generating') return 'input';

  const logs = article.pipeline_logs || [];
  // Use the LAST log entry for accurate current-stage detection
  const lastLog = (logs[logs.length - 1] || '').toLowerCase();

  if (lastLog.includes('improvement') || lastLog.includes('editing for quality')) return 'verify';
  if (lastLog.includes('fact-check') || lastLog.includes('verification pass')) return 'verify';
  if (lastLog.includes('writing article') || lastLog.includes('article written') || lastLog.includes('internal linking')) return 'write';
  if (lastLog.includes('outline') || lastLog.includes('style decision') || lastLog.includes('analyzing competitors')) return 'outline';
  if (lastLog.includes('serp') || lastLog.includes('research') || lastLog.includes('synthesiz') || lastLog.includes('extracting') || lastLog.includes('competitor')) return 'research';
  if (lastLog.includes('starting pipeline') || lastLog.includes('factsheet') || lastLog.includes('user guide')) return 'research';

  return 'input';
}

function PipelineStageTracker({ article }: { article: ArticleSummary }) {
  const currentStage = detectStage(article);
  const isFailed = article.status === 'failed';

  const stageIndex = PIPELINE_STAGES.findIndex(s => s.key === currentStage);

  return (
    <div className="flex items-center gap-1 w-full">
      {PIPELINE_STAGES.map((stage, i) => {
        const isComplete = !isFailed && (stageIndex > i || currentStage === 'complete');
        const isCurrent = !isFailed && stageIndex === i && currentStage !== 'complete';
        const isPending = !isComplete && !isCurrent;

        return (
          <div key={stage.key} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  isFailed && stageIndex === i
                    ? 'bg-red-100 text-red-600 ring-2 ring-red-300'
                    : isComplete
                    ? 'bg-green-500 text-white'
                    : isCurrent
                    ? 'bg-indigo-500 text-white ring-2 ring-indigo-300 animate-pulse'
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                {isComplete ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : isFailed && stageIndex === i ? (
                  <span>&times;</span>
                ) : (
                  stage.icon
                )}
              </div>
              <span className={`text-[10px] mt-0.5 font-medium ${
                isFailed && stageIndex === i
                  ? 'text-red-600'
                  : isComplete
                  ? 'text-green-600'
                  : isCurrent
                  ? 'text-indigo-600'
                  : 'text-gray-400'
              }`}>
                {stage.label}
              </span>
            </div>
            {i < PIPELINE_STAGES.length - 1 && (
              <div className={`h-0.5 flex-1 mx-0.5 mt-[-12px] ${
                isComplete ? 'bg-green-400' : 'bg-gray-200'
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function ContentWriterPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  // Input state
  const [inputMode, setInputMode] = useState<'manual' | 'map'>('manual');
  const [keywords, setKeywords] = useState('');
  const [targetUrls, setTargetUrls] = useState('');  // One URL per line, matching keywords
  const [userGuide, setUserGuide] = useState('');
  const [clientDomain, setClientDomain] = useState('');
  const [noWebsite, setNoWebsite] = useState(false);
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [promptMode, setPromptMode] = useState<'standard' | 'holly'>('standard');
  const [seoKeywordsMode, setSeoKeywordsMode] = useState<'auto' | 'manual'>('auto');
  const [manualSeoKeywordsList, setManualSeoKeywordsList] = useState<string[]>(['']);
  const [maps, setMaps] = useState<MapOption[]>([]);
  const [selectedMapId, setSelectedMapId] = useState<number | null>(null);
  const [topics, setTopics] = useState<TopicOption[]>([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);

  // Article list
  const [articles, setArticles] = useState<ArticleSummary[]>([]);
  const [generating, setGenerating] = useState(false);
  const [selectedModel, setSelectedModel] = useState<{ provider: string; model: string } | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const [articlesPerPage, setArticlesPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>(''); // '' = all, 'completed', 'generating', etc.

  // Article detail / editor
  const [selectedArticle, setSelectedArticle] = useState<ArticleDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorArticleId, setEditorArticleId] = useState<number | null>(null);
  const [editorTitle, setEditorTitle] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareArticleId, setShareArticleId] = useState<number>(0);
  const [shareArticleTitle, setShareArticleTitle] = useState('');

  // Publish
  const [publishMode, setPublishMode] = useState<'standard_post' | 'acf_post' | 'hierarchical_page'>('standard_post');
  const [postStatus, setPostStatus] = useState('draft');
  const [scheduledDate, setScheduledDate] = useState('');
  const [cptSlug, setCptSlug] = useState('');
  const [cpts, setCpts] = useState<CptOption[]>([]);
  const [publishSlug, setPublishSlug] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [wpAuthors, setWpAuthors] = useState<WpAuthor[]>([]);
  const [selectedAuthorId, setSelectedAuthorId] = useState<number | null>(null);
  const [pubMetaTitle, setPubMetaTitle] = useState('');
  const [pubMetaDesc, setPubMetaDesc] = useState('');
  const [useGdriveImages, setUseGdriveImages] = useState(false);

  // Bulk publish
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<number>>(new Set());
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [bulkPublishMode, setBulkPublishMode] = useState<'standard_post' | 'acf_post' | 'hierarchical_page'>('standard_post');
  const [bulkPostStatus, setBulkPostStatus] = useState('draft');
  const [bulkCptSlug, setBulkCptSlug] = useState('');
  const [bulkScheduledDate, setBulkScheduledDate] = useState('');
  const [bulkAuthorId, setBulkAuthorId] = useState<number | null>(null);
  const [bulkUseGdriveImages, setBulkUseGdriveImages] = useState(false);

  // WP + Google Drive status
  const [wpStatus, setWpStatus] = useState<{ exists: boolean; is_verified: boolean; site_url: string | null } | null>(null);
  const [gdriveStatus, setGdriveStatus] = useState<{ configured: boolean; folder_accessible: boolean; project_enabled: boolean } | null>(null);

  // Blank article
  const [blankModal, setBlankModal] = useState(false);
  const [blankTitle, setBlankTitle] = useState('');
  const [blankKeyword, setBlankKeyword] = useState('');
  const [creatingBlank, setCreatingBlank] = useState(false);

  // Send-to-Calendar modal (single article)
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [calendarArticleId, setCalendarArticleId] = useState<number | null>(null);
  const [calendarArticleLabel, setCalendarArticleLabel] = useState('');
  const [calendarDate, setCalendarDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [calendarPriority, setCalendarPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [calendarCategory, setCalendarCategory] = useState('');
  const [calendarNotes, setCalendarNotes] = useState('');
  const [calendarSaving, setCalendarSaving] = useState(false);

  // Bulk Send-to-Calendar modal
  const [bulkCalModalOpen, setBulkCalModalOpen] = useState(false);
  const [bulkCalStart, setBulkCalStart] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [bulkCalEnd, setBulkCalEnd] = useState<string>('');
  const [bulkCalSkipWeekends, setBulkCalSkipWeekends] = useState<boolean>(true);
  const [bulkCalPriority, setBulkCalPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [bulkCalCategory, setBulkCalCategory] = useState('');
  const [bulkCalNotes, setBulkCalNotes] = useState('');
  const [bulkCalSaving, setBulkCalSaving] = useState(false);

  // Image generation
  const [generatingImages, setGeneratingImages] = useState(false);
  const [imageCustomPrompt, setImageCustomPrompt] = useState('');
  const [deletingImages, setDeletingImages] = useState(false);
  const [editingThumbTitle, setEditingThumbTitle] = useState(false);
  const [thumbTitleInput, setThumbTitleInput] = useState('');
  const [savingThumbTitle, setSavingThumbTitle] = useState(false);

  const imagesPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Prompts
  const [showPrompts, setShowPrompts] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  // ── Data Loading ────────────────────────────────────────────────────────

  const loadArticles = useCallback(async (page: number = currentPage, status: string = statusFilter) => {
    if (!projectId) return;
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: articlesPerPage.toString(),
      });
      if (status) {
        params.append('status', status);
      }
      const res = await api.get(`/content-writer/articles/${projectId}?${params}`);
      setArticles(res.data.articles || []);
      setTotalPages(res.data.pages || 1);
      setTotalArticles(res.data.total || 0);
      setCurrentPage(res.data.page || 1);
    } catch { /* ignore */ }
  }, [projectId, currentPage, statusFilter, articlesPerPage]);

  const loadMaps = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/content-writer/maps/${projectId}`);
      setMaps(res.data);
    } catch { /* ignore */ }
  }, [projectId]);

  const loadTopics = useCallback(async () => {
    if (!selectedMapId) return;
    try {
      const res = await api.get(`/content-writer/topics/${selectedMapId}`);
      setTopics(res.data);
    } catch { /* ignore */ }
  }, [selectedMapId]);

  const loadCpts = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/content-writer/cpts/${projectId}`);
      setCpts(res.data);
    } catch { /* ignore */ }
  }, [projectId]);

  const loadWpAuthors = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/content-writer/wp-authors/${projectId}`);
      setWpAuthors(res.data || []);
    } catch { /* ignore */ }
  }, [projectId]);

  const loadWpStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/content-calendar/wp-status/${projectId}`);
      setWpStatus(r.data);
    } catch {
      setWpStatus(null);
    }
  }, [projectId]);

  const loadGdriveStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await api.get(`/content-calendar/gdrive-status/${projectId}`);
      setGdriveStatus(r.data);
      if (r.data?.project_enabled) {
        setUseGdriveImages(true);
        setBulkUseGdriveImages(true);
      }
    } catch {
      setGdriveStatus(null);
    }
  }, [projectId]);

  useEffect(() => {
    loadArticles();
    loadMaps();
    loadCpts();
    loadWpAuthors();
    loadWpStatus();
    loadGdriveStatus();
  }, [loadArticles, loadMaps, loadCpts, loadWpAuthors, loadWpStatus, loadGdriveStatus]);

  // One-time prefill from URL query params (e.g., launched from Content Calendar / Website Audit)
  useEffect(() => {
    const kw = searchParams.get('keyword') || '';
    const title = searchParams.get('title') || '';
    const sourceUrl = searchParams.get('source_url') || '';
    const calendarEntryId = searchParams.get('calendar_entry_id') || '';

    // Prefill primary keyword field
    const primary = (kw || title).trim();
    if (primary) {
      setInputMode('manual');
      setKeywords(prev => prev ? prev : primary);
    }

    // Append context to userGuide if any contextual params are present
    const notes: string[] = [];
    if (title && title !== primary) notes.push(`Title: ${title}`);
    if (sourceUrl) notes.push(`Rewrite source: ${sourceUrl}`);
    if (calendarEntryId) notes.push(`Calendar entry: #${calendarEntryId}`);
    if (notes.length > 0) {
      setUserGuide(prev => prev ? prev : notes.join('\n'));
    }

    // Also prefill target URL (for SEO / canonical context) when rewriting
    if (sourceUrl) {
      setTargetUrls(prev => prev ? prev : sourceUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (selectedMapId) loadTopics(); }, [selectedMapId, loadTopics]);

  // Poll for generating articles — fast 2s interval for live log updates
  useEffect(() => {
    const hasGenerating = articles.some(a => a.status === 'generating');
    if (hasGenerating) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => {
          loadArticles();
          // Also refresh selected article detail if it's currently generating
          if (selectedArticle && selectedArticle.status === 'generating') {
            loadArticleDetail(selectedArticle.id);
          }
        }, 2000);
      }
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        // One final refresh of selected article when generation completes
        if (selectedArticle) {
          loadArticleDetail(selectedArticle.id);
        }
      }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [articles, loadArticles, selectedArticle]);

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [articles]);

  // ── Collect all logs from all articles ──────────────────────────────────
  const allLogs: string[] = [];
  for (const a of [...articles].reverse()) {
    const logs = a.pipeline_logs || [];
    if (logs.length > 0) {
      allLogs.push(`--- ${a.keyword} ---`);
      allLogs.push(...logs);
    }
  }

  // ── Actions ─────────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!projectId) return;
    setGenerating(true);
    setError(null);

    // Parse lines: first value = main keyword, rest (comma-separated) = supporting keywords
    let parsedEntries: { keyword: string; supporting_keywords: string[] }[] = [];
    if (inputMode === 'map' && selectedTopicIds.length > 0) {
      parsedEntries = selectedTopicIds.map(id => {
        const t = topics.find(tp => tp.id === id);
        return { keyword: t?.primary_keyword || t?.topic_name || '', supporting_keywords: [] };
      }).filter(e => e.keyword);
    } else {
      parsedEntries = keywords.split('\n').map(line => {
        const parts = line.split(',').map(p => p.trim()).filter(Boolean);
        if (parts.length === 0) return null;
        return { keyword: parts[0], supporting_keywords: parts.slice(1) };
      }).filter(Boolean) as { keyword: string; supporting_keywords: string[] }[];
    }

    if (parsedEntries.length === 0) {
      setError('Please enter at least one keyword');
      setGenerating(false);
      return;
    }

    if (!noWebsite && !clientDomain.trim()) {
      setError('Client Website is required. If there is no website, check "No Website".');
      setGenerating(false);
      return;
    }

    // Build per-article manual SEO keywords array
    const perArticleSeoKws: string[][] = seoKeywordsMode === 'manual'
      ? manualSeoKeywordsList.map(text => text.split('\n').map(k => k.trim()).filter(Boolean))
      : [];

    // Parse target URLs (one per line, matching parsedEntries by index)
    const targetUrlsArray = targetUrls.split('\n').map(u => u.trim()).filter(Boolean);

    try {
      if (parsedEntries.length === 1) {
        const kws = perArticleSeoKws[0] || [];
        const targetUrl = targetUrlsArray[0] || null;
        await api.post('/content-writer/generate', { project_id: parseInt(projectId), keyword: parsedEntries[0].keyword, supporting_keywords: parsedEntries[0].supporting_keywords, client_domain: clientDomain.trim(), sitemap_url: sitemapUrl.trim(), user_guide: userGuide.trim(), seo_keywords_mode: seoKeywordsMode, manual_seo_keywords: kws, prompt_mode: promptMode, target_url: targetUrl });
      } else {
        await api.post('/content-writer/generate-bulk', { project_id: parseInt(projectId), keywords: parsedEntries, client_domain: clientDomain.trim(), sitemap_url: sitemapUrl.trim(), user_guide: userGuide.trim(), seo_keywords_mode: seoKeywordsMode, per_article_seo_keywords: perArticleSeoKws, prompt_mode: promptMode, target_urls: targetUrlsArray });
      }
      setKeywords('');
      setTargetUrls('');
      setSelectedTopicIds([]);
      setManualSeoKeywordsList(['']);
      await loadArticles();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Generation failed');
    }
    setGenerating(false);
  };

  const loadArticleDetail = async (articleId: number) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/content-writer/articles/detail/${articleId}`);
      setSelectedArticle(res.data);
      setPublishSlug(res.data.slug || '');
      setPubMetaTitle(res.data.meta_title || res.data.title || '');
      setPubMetaDesc(res.data.meta_description || '');
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to load article');
    }
    setLoadingDetail(false);
  };

  const openEditor = (articleId: number, title: string) => {
    setEditorArticleId(articleId);
    setEditorTitle(title);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditorArticleId(null);
    // Refresh data after editor closes
    loadArticles();
    if (selectedArticle && editorArticleId === selectedArticle.id) {
      loadArticleDetail(selectedArticle.id);
    }
  };

  const handleDelete = async (articleId: number) => {
    if (!confirm('Delete this article? This cannot be undone.')) return;
    try {
      await api.delete(`/content-writer/articles/${articleId}`);
      if (selectedArticle?.id === articleId) {
        setSelectedArticle(null);
      }
      await loadArticles();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleRewrite = async (articleId: number) => {
    try {
      await api.post(`/content-writer/articles/${articleId}/rewrite`);
      await loadArticles();
      if (selectedArticle?.id === articleId) {
        setSelectedArticle(null);
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Rewrite failed');
    }
  };

  const handleStop = async (articleId: number) => {
    if (!confirm('Stop this article generation? You can resume it later.')) return;
    try {
      await api.post(`/content-writer/articles/${articleId}/stop`);
      await loadArticles();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Stop failed');
    }
  };

  const handlePublish = async () => {
    if (!selectedArticle) return;
    setPublishing(true);
    setError(null);
    try {
      const selectedCpt = cpts.find(c => c.slug === cptSlug);
      await api.post(`/content-writer/articles/${selectedArticle.id}/publish`, {
        publish_mode: publishMode,
        post_status: postStatus,
        cpt_slug: selectedCpt?.rest_base || cptSlug,
        scheduled_date: postStatus === 'future' ? scheduledDate : '',
        slug: publishSlug,
        author_id: selectedAuthorId || undefined,
        meta_title: pubMetaTitle.trim(),
        meta_description: pubMetaDesc.trim(),
        use_gdrive_images: useGdriveImages,
      });
      await loadArticleDetail(selectedArticle.id);
      await loadArticles();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Publish failed');
    }
    setPublishing(false);
  };

  const handleUnpublish = async () => {
    if (!selectedArticle) return;
    if (!confirm('This will reset the article status so you can republish. The WordPress post will NOT be deleted — remove it manually from WP if needed. Continue?')) return;
    setError(null);
    try {
      await api.post(`/content-writer/articles/${selectedArticle.id}/unpublish`);
      await loadArticleDetail(selectedArticle.id);
      await loadArticles();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Unpublish failed');
    }
  };

  // ── Send-to-Calendar ────────────────────────────────────────────────────
  const openCalendarModal = (article: ArticleSummary) => {
    setCalendarArticleId(article.id);
    setCalendarArticleLabel(article.title || article.keyword);
    setCalendarDate(new Date().toISOString().slice(0, 10));
    setCalendarPriority('medium');
    setCalendarCategory('');
    setCalendarNotes('');
    setCalendarModalOpen(true);
  };

  const handleSendToCalendar = async () => {
    if (!calendarArticleId || !calendarDate) return;
    setCalendarSaving(true);
    setError('');
    try {
      await api.post('/content-calendar/entries/from-article', {
        article_id: calendarArticleId,
        scheduled_date: calendarDate,
        priority: calendarPriority,
        category: calendarCategory,
        notes: calendarNotes,
      });
      setCalendarModalOpen(false);
      alert(`Added to Content Calendar for ${calendarDate}`);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to add to calendar');
    }
    setCalendarSaving(false);
  };

  const openBulkCalendarModal = () => {
    if (bulkSelectedIds.size === 0) return;
    setBulkCalStart(new Date().toISOString().slice(0, 10));
    setBulkCalEnd('');
    setBulkCalSkipWeekends(true);
    setBulkCalPriority('medium');
    setBulkCalCategory('');
    setBulkCalNotes('');
    setBulkCalModalOpen(true);
  };

  const handleBulkSendToCalendar = async () => {
    if (bulkSelectedIds.size === 0 || !bulkCalStart) return;
    setBulkCalSaving(true);
    setError('');
    try {
      const res = await api.post('/content-calendar/entries/from-articles-bulk', {
        article_ids: Array.from(bulkSelectedIds),
        start_date: bulkCalStart,
        end_date: bulkCalEnd || null,
        skip_weekends: bulkCalSkipWeekends,
        priority: bulkCalPriority,
        category: bulkCalCategory,
        notes: bulkCalNotes,
      });
      setBulkCalModalOpen(false);
      setBulkSelectedIds(new Set());
      const { created = [], skipped = [] } = res.data || {};
      const skipMsg = skipped.length > 0 ? ` (${skipped.length} skipped — already on calendar)` : '';
      alert(`Added ${created.length} article(s) to Content Calendar${skipMsg}`);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Bulk send to calendar failed');
    }
    setBulkCalSaving(false);
  };

  const publishableArticles = articles.filter(a => a.status === 'completed' || a.status === 'published');
  const bulkToggle = (id: number) => {
    setBulkSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const bulkSelectAll = () => {
    if (bulkSelectedIds.size === publishableArticles.length) {
      setBulkSelectedIds(new Set());
    } else {
      setBulkSelectedIds(new Set(publishableArticles.map(a => a.id)));
    }
  };
  const handleBulkPublish = async () => {
    if (bulkSelectedIds.size === 0) return;
    if (!confirm(`Publish ${bulkSelectedIds.size} article(s) to WordPress as ${bulkPostStatus}?`)) return;
    setBulkPublishing(true);
    setError(null);
    try {
      const selectedCpt = cpts.find(c => c.slug === bulkCptSlug);
      const res = await api.post('/content-writer/publish-bulk', {
        article_ids: Array.from(bulkSelectedIds),
        publish_mode: bulkPublishMode,
        post_status: bulkPostStatus,
        cpt_slug: selectedCpt?.rest_base || bulkCptSlug,
        scheduled_date: bulkPostStatus === 'future' ? bulkScheduledDate : '',
        author_id: bulkAuthorId || undefined,
        use_gdrive_images: bulkUseGdriveImages,
      });
      const results = res.data.results || [];
      const succeeded = results.filter((r: any) => r.success).length;
      const failed = results.filter((r: any) => !r.success);
      if (failed.length > 0) {
        setError(`Published ${succeeded}/${results.length}. Failed: ${failed.map((f: any) => `#${f.article_id}: ${f.error}`).join(', ')}`);
      }
      setBulkSelectedIds(new Set());
      await loadArticles();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Bulk publish failed');
    }
    setBulkPublishing(false);
  };

  const handleDeleteImages = async (articleId: number) => {
    if (!confirm('Delete all generated images for this article?')) return;
    setDeletingImages(true);
    try {
      await api.delete(`/content-writer/articles/${articleId}/images`);
      await loadArticleDetail(articleId);
      await loadArticles();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete images');
    } finally {
      setDeletingImages(false);
    }
  };

  const handleGenerateImages = async (articleId: number) => {
    setGeneratingImages(true);
    setError(null);
    try {
      await api.post(`/content-writer/articles/${articleId}/generate-images`, { custom_prompt: imageCustomPrompt });
      // Poll for completion
      if (imagesPollRef.current) clearInterval(imagesPollRef.current);
      imagesPollRef.current = setInterval(async () => {
        try {
          const res = await api.get(`/content-writer/articles/${articleId}/images`);
          if (res.data.images_status === 'completed' || res.data.images_status === 'failed') {
            if (imagesPollRef.current) clearInterval(imagesPollRef.current);
            setGeneratingImages(false);
            // Reload article detail to get updated content_html with injected infographics
            await loadArticleDetail(articleId);
            await loadArticles();
          }
        } catch { /* ignore polling errors */ }
      }, 5000);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Image generation failed');
      setGeneratingImages(false);
    }
  };

  // Cleanup image poll on unmount
  useEffect(() => {
    return () => { if (imagesPollRef.current) clearInterval(imagesPollRef.current); };
  }, []);

  const toggleTopicSelection = (topicId: number) => {
    setSelectedTopicIds(prev =>
      prev.includes(topicId) ? prev.filter(id => id !== topicId) : [...prev, topicId]
    );
  };

  const selectAllTopics = () => {
    if (selectedTopicIds.length === topics.length) {
      setSelectedTopicIds([]);
    } else {
      setSelectedTopicIds(topics.map(t => t.id));
    }
  };

  const createBlankArticle = async () => {
    if (!projectId || !blankTitle.trim()) return;
    setCreatingBlank(true);
    try {
      const fd = new FormData();
      fd.append('project_id', projectId);
      fd.append('title', blankTitle.trim());
      fd.append('keyword', blankKeyword.trim() || blankTitle.trim());
      const res = await api.post('/content-writer/articles/create-blank', fd);
      setBlankModal(false);
      setBlankTitle('');
      setBlankKeyword('');
      await loadArticles();
      // Open editor immediately
      openEditor(res.data.id, blankTitle.trim());
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to create blank article');
    }
    setCreatingBlank(false);
  };

  // ── Status helpers ──────────────────────────────────────────────────────

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      generating: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      published: 'bg-blue-100 text-blue-700',
      stopped: 'bg-gray-100 text-gray-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
        {status === 'generating' && <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full mr-1 animate-pulse" />}
        {status}
      </span>
    );
  };

  if (!projectId) {
    return (
      <Layout>
        <div className="text-center py-20 text-gray-500">
          <p className="text-lg font-medium">No Project Selected</p>
          <p className="mt-2">Select a project from the Projects page to use Content Writer.</p>
        </div>
      </Layout>
    );
  }

  const wpMissing = wpStatus !== null && !wpStatus.exists;
  const gdriveAvailable = !!gdriveStatus?.configured && !!gdriveStatus?.folder_accessible;

  return (
    <Layout projectId={parseInt(projectId)}>
      <div className="flex gap-6 max-w-[1400px] mx-auto">
        {/* ── Main Pipeline (70%) ── */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* WordPress credentials banner */}
          {wpMissing && (
            <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start justify-between gap-3">
              <div className="text-sm text-yellow-900">
                <div className="font-semibold mb-0.5">WordPress credentials are missing.</div>
                <div className="text-yellow-800">
                  Publishing and scheduling are disabled until WP credentials are saved for this project.
                </div>
              </div>
              <button
                onClick={() => window.location.assign(`/projects/${projectId}/liveupdate/credentials`)}
                className="shrink-0 px-3 py-1.5 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md"
              >
                Configure now
              </button>
            </div>
          )}
          {/* Header */}
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Content Writer</h1>
              <p className="text-gray-500 mt-1">Generate, edit, and publish SEO articles — automated pipeline.</p>
            </div>
            <div className="flex items-center gap-2">
              <a
                href="https://drive.google.com/file/d/1WEjp577XcDXXYTnn-8-nzkP4VdYP0-yE/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors"
                title="Watch tutorial video"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                Watch Tutorial
              </a>
              <button
                onClick={() => setShowPrompts(!showPrompts)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${showPrompts ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {showPrompts ? 'Hide Prompts' : 'Edit Prompts'}
              </button>
            </div>
          </div>

          {/* Prompts Editor */}
          {showPrompts && (
            <div className="mb-2">
              <PromptsEditor
                systemType="contentwriter"
                title="Content Writer Prompts"
                subtitle="Edit the prompts used by each stage of the content writing pipeline."
                categories={{
                  order: ['pipeline', 'linking'],
                  names: { pipeline: 'Pipeline Stages', linking: 'Internal Linking' },
                  icons: { pipeline: '\u2699\uFE0F', linking: '\uD83D\uDD17' }
                }}
                onClose={() => setShowPrompts(false)}
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex justify-between items-center">
              {error}
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-4">&times;</button>
            </div>
          )}

          {/* ═══════════ INPUT SECTION ═══════════ */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Generate Articles</h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-500">Prompt:</label>
                  <select
                    value={promptMode}
                    onChange={e => setPromptMode(e.target.value as 'standard' | 'holly')}
                    className="px-2.5 py-1.5 rounded-lg text-sm font-medium border border-gray-300 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="standard">Standard Pipeline</option>
                    <option value="holly">Holly Prompt</option>
                  </select>
                </div>
                <ModelSelector value={selectedModel} onChange={setSelectedModel} />
                <div className="flex gap-2">
                  <button
                    onClick={() => setInputMode('manual')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${inputMode === 'manual' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    Manual Input
                  </button>
                  <button
                    onClick={() => setInputMode('map')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${inputMode === 'map' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    From Topical Map
                  </button>
                </div>
              </div>
            </div>

            {/* Client Domain & Sitemap — shared across both modes */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-gray-700">
                    Client Website {!noWebsite && <span className="text-red-500">*</span>} <span className="text-gray-400 font-normal">(for internal linking)</span>
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={noWebsite}
                      onChange={e => {
                        setNoWebsite(e.target.checked);
                        if (e.target.checked) setClientDomain('');
                      }}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    No Website
                  </label>
                </div>
                <input
                  type="text"
                  value={clientDomain}
                  onChange={e => setClientDomain(e.target.value)}
                  placeholder="e.g. example.com"
                  disabled={noWebsite}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${noWebsite ? 'bg-gray-100 border-gray-200 text-gray-400' : 'border-gray-300'}`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sitemap URL <span className="text-gray-400 font-normal">(optional — direct link to sitemap.xml)</span>
                </label>
                <input
                  type="text"
                  value={sitemapUrl}
                  onChange={e => setSitemapUrl(e.target.value)}
                  placeholder="e.g. https://example.com/sitemap.xml"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* User Guide — shared across both modes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Guide <span className="text-gray-400 font-normal">(optional — custom instructions AI will follow while writing)</span>
              </label>
              <textarea
                value={userGuide}
                onChange={e => setUserGuide(e.target.value)}
                placeholder={"e.g. Focus on outpatient treatment options. Mention our new telehealth program. Target audience is families of adults struggling with addiction. Don't use the word 'facility' — use 'center' instead."}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              />
            </div>

            {/* Holly Prompt info banner */}
            {promptMode === 'holly' && (
              <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-800 font-medium">Holly Prompt Mode</p>
                <p className="text-xs text-purple-600 mt-0.5">Single-call article generation. No SERP research, no Ahrefs, no outline stage. Generates a complete SEO article in one LLM call using the Holly prompt structure. Client context is pulled from the FactSheet.</p>
              </div>
            )}

            {/* SEO Keywords Mode — hidden in Holly mode */}
            {promptMode !== 'holly' && <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Ahrefs SEO Keywords <span className="text-gray-400 font-normal">(top keywords the article must cover for ranking)</span>
                </label>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setSeoKeywordsMode('auto')}
                    className={`px-2.5 py-1 rounded text-xs font-medium ${seoKeywordsMode === 'auto' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  >
                    Auto (Ahrefs API)
                  </button>
                  <button
                    onClick={() => setSeoKeywordsMode('manual')}
                    className={`px-2.5 py-1 rounded text-xs font-medium ${seoKeywordsMode === 'manual' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  >
                    Manual
                  </button>
                </div>
              </div>
              {seoKeywordsMode === 'auto' ? (
                <p className="text-xs text-gray-400">Top 20 keywords + PAA questions fetched automatically from Ahrefs per article (based on each primary keyword). Best for bulk. Requires Ahrefs API units.</p>
              ) : (
                <div className="space-y-2">
                  {manualSeoKeywordsList.map((text, idx) => {
                    const articleLines = keywords.split('\n').filter(k => k.trim());
                    const articleLabel = articleLines[idx]
                      ? articleLines[idx].split(',')[0].trim()
                      : `Article ${idx + 1}`;
                    return (
                      <div key={idx} className="relative">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-medium text-gray-500">
                            {manualSeoKeywordsList.length > 1 ? `Article ${idx + 1}: ${articleLabel}` : 'SEO Keywords'}
                          </span>
                          {manualSeoKeywordsList.length > 1 && (
                            <button
                              onClick={() => {
                                const updated = manualSeoKeywordsList.filter((_, i) => i !== idx);
                                setManualSeoKeywordsList(updated.length ? updated : ['']);
                              }}
                              className="text-xs text-red-400 hover:text-red-600"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <textarea
                          value={text}
                          onChange={e => {
                            const updated = [...manualSeoKeywordsList];
                            updated[idx] = e.target.value;
                            setManualSeoKeywordsList(updated);
                          }}
                          placeholder={"keyword 1\nkeyword 2\nkeyword 3\n..."}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                        />
                        {text.trim() && (
                          <span className="absolute bottom-2 right-2 text-[10px] text-gray-300">
                            {text.split('\n').filter(k => k.trim()).length} keywords
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <button
                    onClick={() => setManualSeoKeywordsList([...manualSeoKeywordsList, ''])}
                    className="flex items-center gap-1 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-xs font-medium text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                  >
                    <span className="text-base leading-none">+</span> Add keywords for another article
                  </button>
                  <p className="text-xs text-gray-400">
                    One keyword per line in each field. Each field maps to an article by position (1st field → 1st article, 2nd → 2nd, etc.). Articles without a matching field will skip manual keywords.
                  </p>
                </div>
              )}
            </div>}

            {inputMode === 'manual' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Article Input <span className="text-gray-400 font-normal">(one article per line)</span>
                  </label>
                  <textarea
                    value={keywords}
                    onChange={e => setKeywords(e.target.value)}
                    placeholder={"primary keyword, secondary keyword, secondary keyword\nprimary keyword, secondary keyword, secondary keyword\nprimary keyword, secondary keyword, secondary keyword"}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">First value on each line = primary keyword (article focus). Comma-separated values after = secondary/supporting keywords.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target URLs <span className="text-gray-400 font-normal">(one URL per line, matching articles above)</span>
                  </label>
                  <textarea
                    value={targetUrls}
                    onChange={e => setTargetUrls(e.target.value)}
                    placeholder={"https://example.com/blog/first-article/\nhttps://example.com/blog/second-article/\nhttps://example.com/blog/third-article/"}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Used in AI citation block and WordPress publish. Line 1 = Article 1, Line 2 = Article 2, etc. Optional — auto-generated from keyword if left blank.
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    {keywords.split('\n').filter(k => k.trim()).length} article(s) — each line runs the full pipeline automatically
                  </p>
                  <button
                    onClick={handleGenerate}
                    disabled={generating || !keywords.trim()}
                    className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {generating && <Spinner />}
                    Generate {keywords.split('\n').filter(k => k.trim()).length > 1 ? 'All' : 'Article'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Map</label>
                    <select
                      value={selectedMapId || ''}
                      onChange={e => { setSelectedMapId(e.target.value ? parseInt(e.target.value) : null); setSelectedTopicIds([]); }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">Choose a map...</option>
                      {maps.map(m => (
                        <option key={m.id} value={m.id}>{m.name} ({m.total_topics} topics)</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    {selectedMapId && topics.length > 0 && (
                      <button onClick={selectAllTopics} className="px-3 py-2 text-sm text-indigo-600 hover:text-indigo-800">
                        {selectedTopicIds.length === topics.length ? 'Deselect All' : `Select All (${topics.length})`}
                      </button>
                    )}
                  </div>
                </div>

                {selectedMapId && topics.length > 0 && (
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left w-8">
                            <input
                              type="checkbox"
                              checked={selectedTopicIds.length === topics.length && topics.length > 0}
                              onChange={selectAllTopics}
                              className="rounded border-gray-300"
                            />
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">Topic</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">Keyword</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">Cluster</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">Tier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topics.map(t => (
                          <tr
                            key={t.id}
                            onClick={() => toggleTopicSelection(t.id)}
                            className={`cursor-pointer hover:bg-gray-50 ${selectedTopicIds.includes(t.id) ? 'bg-indigo-50' : ''}`}
                          >
                            <td className="px-3 py-1.5">
                              <input
                                type="checkbox"
                                checked={selectedTopicIds.includes(t.id)}
                                onChange={() => toggleTopicSelection(t.id)}
                                className="rounded border-gray-300"
                              />
                            </td>
                            <td className="px-3 py-1.5 text-gray-800">{t.topic_name}</td>
                            <td className="px-3 py-1.5 text-gray-600">{t.primary_keyword}</td>
                            <td className="px-3 py-1.5 text-gray-500">{t.cluster_name}</td>
                            <td className="px-3 py-1.5">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                t.tier === 'pillar' ? 'bg-purple-100 text-purple-700' :
                                t.tier === 'cluster' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{t.tier}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    {selectedTopicIds.length} topic(s) selected
                  </p>
                  <button
                    onClick={handleGenerate}
                    disabled={generating || selectedTopicIds.length === 0}
                    className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {generating && <Spinner />}
                    Generate {selectedTopicIds.length > 1 ? `${selectedTopicIds.length} Articles` : 'Article'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ═══════════ ARTICLES LIST ═══════════ */}
          {articles.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
              <p className="text-gray-500 mb-3">No articles yet. Generate from keywords above or create a blank article.</p>
              <button
                onClick={() => setBlankModal(true)}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg inline-flex items-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Create Blank Article
              </button>
            </div>
          )}
          {articles.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {publishableArticles.length > 0 && (
                    <input
                      type="checkbox"
                      checked={bulkSelectedIds.size === publishableArticles.length && publishableArticles.length > 0}
                      onChange={bulkSelectAll}
                      className="w-4 h-4 text-indigo-600 rounded border-gray-300"
                      title="Select all publishable articles"
                    />
                  )}
                  <h2 className="text-lg font-semibold text-gray-900">
                    Generated Articles ({totalArticles} total{statusFilter ? `, showing ${statusFilter}` : ''})
                  </h2>
                  <select
                    value={statusFilter}
                    onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); loadArticles(1, e.target.value); }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="generating">Generating</option>
                    <option value="stopped">Stopped</option>
                    <option value="failed">Failed</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                <button
                  onClick={() => setBlankModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Create Blank Article
                </button>
              </div>

              {/* Bulk Publish Bar */}
              {bulkSelectedIds.size > 0 && (
                <div className="px-6 py-3 bg-indigo-50 border-b border-indigo-200 flex items-end gap-3 flex-wrap">
                  <span className="text-sm font-medium text-indigo-800 self-center">{bulkSelectedIds.size} selected</span>
                  <div>
                    <label className="block text-xs font-medium text-indigo-600 mb-0.5">Mode</label>
                    <select value={bulkPublishMode} onChange={e => setBulkPublishMode(e.target.value as any)} className="px-2 py-1 border border-indigo-300 rounded text-sm bg-white">
                      <option value="standard_post">Standard Post</option>
                      <option value="hierarchical_page">Page (Hierarchical)</option>
                      <option value="acf_post">ACF Custom Post</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-indigo-600 mb-0.5">Status</label>
                    <select value={bulkPostStatus} onChange={e => { setBulkPostStatus(e.target.value); if (e.target.value !== 'future') setBulkScheduledDate(''); }} className="px-2 py-1 border border-indigo-300 rounded text-sm bg-white">
                      <option value="draft">Draft</option>
                      <option value="publish">Publish</option>
                      <option value="pending">Pending Review</option>
                      <option value="future">Schedule</option>
                    </select>
                  </div>
                  {bulkPostStatus === 'future' && (
                    <div>
                      <label className="block text-xs font-medium text-indigo-600 mb-0.5">Schedule Date</label>
                      <input type="datetime-local" value={bulkScheduledDate} onChange={e => setBulkScheduledDate(e.target.value)} min={new Date().toISOString().slice(0, 16)} className="px-2 py-1 border border-indigo-300 rounded text-sm bg-white" />
                    </div>
                  )}
                  {bulkPublishMode === 'acf_post' && (
                    <div>
                      <label className="block text-xs font-medium text-indigo-600 mb-0.5">CPT</label>
                      <select value={bulkCptSlug} onChange={e => setBulkCptSlug(e.target.value)} className="px-2 py-1 border border-indigo-300 rounded text-sm bg-white">
                        <option value="">Select CPT...</option>
                        {cpts.map(c => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-indigo-600 mb-0.5">Author</label>
                    <select value={bulkAuthorId || ''} onChange={e => setBulkAuthorId(e.target.value ? Number(e.target.value) : null)} className="px-2 py-1 border border-indigo-300 rounded text-sm bg-white">
                      <option value="">Default</option>
                      {wpAuthors.map(a => (<option key={a.id} value={a.id}>{a.name}</option>))}
                    </select>
                  </div>
                  {gdriveAvailable && (
                    <label className="flex items-center gap-1.5 text-xs text-indigo-800 bg-white px-2 py-1 rounded border border-indigo-200 cursor-pointer self-end h-[30px]">
                      <input
                        type="checkbox"
                        checked={bulkUseGdriveImages}
                        onChange={e => setBulkUseGdriveImages(e.target.checked)}
                        className="h-3.5 w-3.5"
                      />
                      Drive (lh3)
                    </label>
                  )}
                  <button
                    onClick={handleBulkPublish}
                    disabled={bulkPublishing || wpMissing || (bulkPublishMode === 'acf_post' && !bulkCptSlug) || (bulkPostStatus === 'future' && !bulkScheduledDate)}
                    className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    title={wpMissing ? 'Configure WordPress credentials first' : ''}
                  >
                    {bulkPublishing && <Spinner />}
                    Publish {bulkSelectedIds.size} Article{bulkSelectedIds.size > 1 ? 's' : ''}
                  </button>
                  <button
                    onClick={openBulkCalendarModal}
                    className="px-4 py-1.5 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-700 flex items-center gap-1"
                    title="Add selected articles to the Content Calendar"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Send {bulkSelectedIds.size} to Calendar
                  </button>
                  <button onClick={() => setBulkSelectedIds(new Set())} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                </div>
              )}
              <div className="divide-y divide-gray-100">
                {articles.map(article => (
                  <div
                    key={article.id}
                    className={`px-6 py-3 hover:bg-gray-50 cursor-pointer ${selectedArticle?.id === article.id ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''}`}
                    onClick={() => article.status !== 'generating' && loadArticleDetail(article.id)}
                  >
                    <div className="flex items-center justify-between">
                      {(article.status === 'completed' || article.status === 'published') && (
                        <input
                          type="checkbox"
                          checked={bulkSelectedIds.has(article.id)}
                          onChange={(e) => { e.stopPropagation(); bulkToggle(article.id); }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-indigo-600 rounded border-gray-300 mr-3 flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900 truncate">{article.title || article.keyword}</span>
                          {statusBadge(article.status)}
                          {article.is_edited && (
                            <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded text-xs">edited</span>
                          )}
                          {article.prompt_mode === 'holly' && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-xs font-medium" title="Holly Prompt">Holly</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>{article.keyword}</span>
                          {article.word_count > 0 && <span>{article.word_count} words</span>}
                          {article.created_at && <span>{new Date(article.created_at).toLocaleDateString()}</span>}
                          {article.wp_url && <a href={article.wp_url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline" onClick={e => e.stopPropagation()}>View on WP</a>}
                        </div>
                        {article.status === 'failed' && article.error_message && (
                          <p className="text-xs text-red-500 mt-1 truncate">{article.error_message}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {(article.status === 'completed' || article.status === 'published') && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); openEditor(article.id, article.title || article.keyword); }}
                              className="px-2.5 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg"
                              title="Edit in rich text editor"
                            >
                              Edit
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setShareArticleId(article.id); setShareArticleTitle(article.title || article.keyword); setShareModalOpen(true); }}
                              className="px-2.5 py-1 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg"
                              title="Share article"
                            >
                              Share
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); openCalendarModal(article); }}
                              className="px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg"
                              title="Add to Content Calendar"
                            >
                              Calendar
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRewrite(article.id); }}
                              className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
                              title="Rewrite article"
                            >
                              Rewrite
                            </button>
                          </>
                        )}
                        {article.status === 'generating' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleStop(article.id); }}
                            className="px-2.5 py-1 text-xs font-medium text-yellow-600 bg-yellow-50 hover:bg-yellow-100 rounded-lg"
                            title="Stop generation"
                          >
                            Stop
                          </button>
                        )}
                        {article.status === 'failed' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRewrite(article.id); }}
                            className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg"
                          >
                            Retry
                          </button>
                        )}
                        {article.status === 'stopped' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRewrite(article.id); }}
                            className="px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg"
                            title="Resume generation"
                          >
                            Resume
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(article.id); }}
                          className="px-2.5 py-1 text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                          title="Delete article"
                        >
                          &times;
                        </button>
                      </div>
                    </div>

                    {/* Pipeline Stage Tracker */}
                    {(article.status === 'generating' || article.status === 'failed') && (
                      <div className="mt-3 mb-1">
                        <PipelineStageTracker article={article} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages} • Showing {articles.length} of {totalArticles} articles
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { const prev = currentPage - 1; setCurrentPage(prev); loadArticles(prev); }}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      // Show first 2, last 2, and current page
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => { setCurrentPage(pageNum); loadArticles(pageNum); }}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                            currentPage === pageNum
                              ? 'bg-indigo-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => { const next = currentPage + 1; setCurrentPage(next); loadArticles(next); }}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══════════ ARTICLE DETAIL / EDITOR ═══════════ */}
          {loadingDetail && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <Spinner />
              <p className="text-gray-500 mt-2">Loading article...</p>
            </div>
          )}

          {selectedArticle && !loadingDetail && (
            <div className="bg-white rounded-xl border border-gray-200">
              {/* Article Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{selectedArticle.title || selectedArticle.keyword}</h2>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <span>Keyword: {selectedArticle.keyword}</span>
                      {selectedArticle.supporting_keywords && selectedArticle.supporting_keywords.length > 0 && (
                        <span className="text-gray-400">+ {selectedArticle.supporting_keywords.length} supporting</span>
                      )}
                      <span>{selectedArticle.word_count} words</span>
                      {statusBadge(selectedArticle.status)}
                      {selectedArticle.is_edited && (
                        <span className="text-orange-500 text-xs">
                          Edited {selectedArticle.edited_at ? new Date(selectedArticle.edited_at).toLocaleString() : ''} by {selectedArticle.edited_by_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedArticle.content_html && (
                      <>
                        <button
                          onClick={() => openEditor(selectedArticle.id, selectedArticle.title || selectedArticle.keyword)}
                          className="px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => { setShareArticleId(selectedArticle.id); setShareArticleTitle(selectedArticle.title || selectedArticle.keyword); setShareModalOpen(true); }}
                          className="px-3 py-1.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg"
                        >
                          Share
                        </button>
                        <button
                          onClick={() => handleRewrite(selectedArticle.id)}
                          className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
                        >
                          Rewrite
                        </button>
                        <button
                          onClick={() => handleGenerateImages(selectedArticle.id)}
                          disabled={generatingImages || selectedArticle.images_status === 'generating'}
                          className="px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg disabled:opacity-50 flex items-center gap-1.5"
                          title="Generate thumbnail + infographic images"
                        >
                          {(generatingImages || selectedArticle.images_status === 'generating') && <Spinner />}
                          {selectedArticle.images_status === 'completed' ? 'Regenerate Images' : 'Generate Images'}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setSelectedArticle(null)}
                      className="px-2 py-1 text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  </div>
                </div>

                {/* Pipeline completed summary */}
                {(selectedArticle.status === 'completed' || selectedArticle.status === 'published') && (
                  <div className="mt-3">
                    <PipelineStageTracker article={selectedArticle} />
                  </div>
                )}
              </div>

              {/* Meta info row */}
              {selectedArticle.meta_description && (
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 text-sm">
                  <span className="text-gray-500">Meta:</span>{' '}
                  <span className="text-gray-700">{selectedArticle.meta_description}</span>
                </div>
              )}

              {/* Article metadata stats */}
              {selectedArticle.article_metadata && (
                <div className="px-6 py-3 border-b border-gray-200 flex items-center gap-6 text-xs">
                  {selectedArticle.article_metadata.internal_links !== undefined && (
                    <div><span className="text-gray-500">Internal Links:</span> <strong>{selectedArticle.article_metadata.internal_links}</strong></div>
                  )}
                  {selectedArticle.article_metadata.external_links !== undefined && (
                    <div><span className="text-gray-500">External Links:</span> <strong>{selectedArticle.article_metadata.external_links}</strong></div>
                  )}
                  {selectedArticle.article_metadata.faq_count !== undefined && (
                    <div><span className="text-gray-500">FAQs:</span> <strong>{selectedArticle.article_metadata.faq_count}</strong></div>
                  )}
                  {selectedArticle.article_metadata.reading_level && (
                    <div><span className="text-gray-500">Reading Level:</span> <strong>{selectedArticle.article_metadata.reading_level}</strong></div>
                  )}
                  {selectedArticle.article_metadata.information_gain_section && (
                    <div><span className="text-gray-500">Info Gain:</span> <strong>{selectedArticle.article_metadata.information_gain_section}</strong></div>
                  )}
                </div>
              )}

              {/* Thumbnail & Images Section */}
              {(selectedArticle.images_status === 'generating' || selectedArticle.thumbnail_url || selectedArticle.images_status === 'completed' || selectedArticle.images_status === 'failed') && (
                <div className="px-6 py-4 border-b border-gray-200 bg-amber-50/30">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      Article Images
                      {selectedArticle.images_status === 'generating' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full"><Spinner /> Generating...</span>
                      )}
                      {selectedArticle.images_status === 'completed' && (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Ready</span>
                      )}
                      {selectedArticle.images_status === 'failed' && (
                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">Failed</span>
                      )}
                    </h3>
                    {selectedArticle.images_status === 'completed' && (
                      <button
                        onClick={() => handleDeleteImages(selectedArticle.id)}
                        disabled={deletingImages}
                        className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg disabled:opacity-50"
                      >
                        {deletingImages ? 'Deleting...' : 'Delete All Images'}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-4 overflow-x-auto items-start">
                    {selectedArticle.thumbnail_url && (
                      <div className="flex-shrink-0">
                        <p className="text-xs text-gray-500 mb-1 font-medium">Thumbnail (Featured Image)</p>
                        <img
                          src={selectedArticle.thumbnail_url}
                          alt="Article thumbnail"
                          className="w-72 h-auto object-cover rounded-lg border border-gray-200 shadow-sm"
                        />
                        {/* Editable thumbnail title */}
                        <div className="mt-1.5 max-w-[288px]">
                          {editingThumbTitle ? (
                            <div className="flex gap-1">
                              <input
                                value={thumbTitleInput}
                                onChange={(e) => setThumbTitleInput(e.target.value)}
                                className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
                                placeholder="Thumbnail title..."
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter' && thumbTitleInput.trim()) {
                                    setSavingThumbTitle(true);
                                    try {
                                      const res = await api.put(`/content-writer/articles/${selectedArticle.id}/thumbnail-title`, { title: thumbTitleInput.trim() });
                                      await loadArticleDetail(selectedArticle.id);
                                      setEditingThumbTitle(false);
                                    } catch { }
                                    setSavingThumbTitle(false);
                                  } else if (e.key === 'Escape') {
                                    setEditingThumbTitle(false);
                                  }
                                }}
                              />
                              <button
                                onClick={async () => {
                                  if (!thumbTitleInput.trim()) return;
                                  setSavingThumbTitle(true);
                                  try {
                                    await api.put(`/content-writer/articles/${selectedArticle.id}/thumbnail-title`, { title: thumbTitleInput.trim() });
                                    await loadArticleDetail(selectedArticle.id);
                                    setEditingThumbTitle(false);
                                  } catch { }
                                  setSavingThumbTitle(false);
                                }}
                                disabled={savingThumbTitle}
                                className="text-xs px-2 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
                              >
                                {savingThumbTitle ? '...' : 'Save'}
                              </button>
                              <button onClick={() => setEditingThumbTitle(false)} className="text-xs px-1 text-gray-400 hover:text-gray-600">&times;</button>
                            </div>
                          ) : (
                            <p
                              className="text-xs text-gray-500 cursor-pointer hover:text-blue-600 truncate"
                              title="Click to edit thumbnail title"
                              onClick={() => { setThumbTitleInput(selectedArticle.thumbnail_title || selectedArticle.title || ''); setEditingThumbTitle(true); }}
                            >
                              Title: {selectedArticle.thumbnail_title || selectedArticle.title || 'Click to set'} <span className="text-blue-400 text-[10px]">edit</span>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedArticle.infographic_urls && selectedArticle.infographic_urls.length > 0 && (
                      <>
                        {selectedArticle.infographic_urls.map((info: any, idx: number) => (
                          <div key={idx} className="flex-shrink-0">
                            <p className="text-xs text-gray-500 mb-1 font-medium">Infographic {idx + 1}</p>
                            <img
                              src={info.base64_uri || info.url}
                              alt={info.description || `Infographic ${idx + 1}`}
                              className="w-36 h-auto object-contain rounded-lg border border-gray-200 shadow-sm"
                            />
                            <p className="text-xs text-gray-400 mt-1 max-w-[144px] truncate" title={info.description}>
                              {info.description}
                            </p>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  {/* Custom instructions for regeneration */}
                  {selectedArticle.images_status === 'completed' && (
                    <div className="mt-3 flex gap-2 items-end">
                      <textarea
                        value={imageCustomPrompt}
                        onChange={(e) => setImageCustomPrompt(e.target.value)}
                        placeholder="Custom instructions for regeneration (e.g., 'use blue color scheme', 'focus on statistics in section 2')..."
                        className="flex-1 text-xs border border-gray-300 rounded-lg px-3 py-2 resize-none h-16 focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                      />
                      <button
                        onClick={() => handleGenerateImages(selectedArticle.id)}
                        disabled={generatingImages}
                        className="px-3 py-2 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg disabled:opacity-50 whitespace-nowrap h-16"
                      >
                        {generatingImages ? 'Regenerating...' : 'Regenerate with Instructions'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Content area */}
              <div className="px-6 py-4">
                {selectedArticle.content_html ? (
                  <div className="prose prose-sm max-w-none max-h-[600px] overflow-y-auto border border-gray-200 rounded-lg p-4 article-content">
                    <style>{`
                      .article-content table { width: 100%; border-collapse: collapse; margin: 1.5em 0; }
                      .article-content th, .article-content td { border: 1px solid #d1d5db; padding: 10px 14px; text-align: left; }
                      .article-content th { background-color: #f3f4f6; font-weight: 600; }
                      .article-content tr:nth-child(even) { background-color: #f9fafb; }
                      .article-content .callout { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 14px 18px; margin: 1.5em 0; border-radius: 6px; }
                      .article-content .note-box { background: #eff6ff; border-left: 4px solid #3b82f6; padding: 14px 18px; margin: 1.5em 0; border-radius: 6px; color: #1e40af; font-size: 0.95rem; }
                      .article-content blockquote { border-left: 4px solid #6366f1; background: #eef2ff; padding: 14px 18px; margin: 1.5em 0; border-radius: 4px; }
                      .article-content .article-infographic { margin: 2rem auto; text-align: center; }
                      .article-content .article-infographic img { max-width: 100%; height: auto; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
                      .article-content .article-infographic figcaption { font-size: 0.85rem; color: #6b7280; margin-top: 0.5rem; font-style: italic; }
                      .article-content h1 { font-size: 1.75rem; font-weight: 700; color: #111827; margin: 0 0 1rem 0; padding-bottom: 0.5rem; border-bottom: 2px solid #e5e7eb; }
                      .article-content h2 { font-size: 1.35rem; font-weight: 600; color: #1f2937; margin: 1.75rem 0 0.75rem 0; padding-top: 0.75rem; border-top: 1px solid #f3f4f6; }
                      .article-content h3 { font-size: 1.1rem; font-weight: 600; color: #374151; margin: 1.25rem 0 0.5rem 0; }
                      .article-content p { margin: 0 0 0.75rem 0; line-height: 1.7; color: #374151; }
                    `}</style>
                    <div dangerouslySetInnerHTML={{ __html: (selectedArticle.content_html || '').replace(/src="(\/api\/[^"]+)"/g, (_, url) => `src="${url}${url.includes('?') ? '&' : '?'}token=${localStorage.getItem('auth_token') || ''}"`) }} />
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm py-8 text-center">No content available</p>
                )}
              </div>

              {/* Publish section */}
              {selectedArticle.content_html && (selectedArticle.status === 'completed' || selectedArticle.status === 'published') && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">Publish to WordPress</h3>

                  {wpMissing && (
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-2 text-xs text-yellow-900 flex items-center justify-between gap-2">
                      <span>
                        <strong>WordPress credentials missing.</strong> Configure them in LiveUpdate → Credentials before publishing.
                      </span>
                      <button
                        type="button"
                        onClick={() => window.location.assign(`/projects/${projectId}/liveupdate/credentials`)}
                        className="shrink-0 px-2 py-1 text-xs font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded"
                      >
                        Configure
                      </button>
                    </div>
                  )}

                  {publishMode === 'hierarchical_page' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
                      Publishing as a <strong>WordPress Page</strong> with hierarchy. Use <code>/</code> in the slug (e.g. <code>addiction/alcohol/depression</code>). Parent pages must already exist in WordPress.
                    </div>
                  )}

                  {/* Row 1: Slug + Author */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Slug (URL path)</label>
                      <input
                        type="text"
                        value={publishSlug}
                        onChange={e => {
                          // Always allow slashes for structured URL paths
                          const val = e.target.value.toLowerCase().replace(/[^a-z0-9-/]/g, '').replace(/\/+/g, '/').replace(/-+/g, '-');
                          setPublishSlug(val);
                        }}
                        placeholder="e.g. addiction/drug/whippets-drug"
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Author</label>
                      <select
                        value={selectedAuthorId || ''}
                        onChange={e => setSelectedAuthorId(e.target.value ? Number(e.target.value) : null)}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="">Default Author</option>
                        {wpAuthors.map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 2: Meta Title + Meta Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">SEO Meta Title <span className="text-gray-400 font-normal">(Yoast & Rank Math)</span></label>
                    <input
                      type="text"
                      value={pubMetaTitle}
                      onChange={e => setPubMetaTitle(e.target.value)}
                      placeholder="SEO title for search engines..."
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                      maxLength={70}
                    />
                    <div className="text-right text-[10px] text-gray-400 mt-0.5">{pubMetaTitle.length}/70</div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">SEO Meta Description <span className="text-gray-400 font-normal">(Yoast & Rank Math)</span></label>
                    <textarea
                      value={pubMetaDesc}
                      onChange={e => setPubMetaDesc(e.target.value)}
                      placeholder="Meta description for search results..."
                      rows={2}
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm resize-none"
                      maxLength={160}
                    />
                    <div className="text-right text-[10px] text-gray-400 mt-0.5">{pubMetaDesc.length}/160</div>
                  </div>

                  {/* Row 3: Mode, Status, Schedule, CPT, Publish button */}
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Mode</label>
                      <select
                        value={publishMode}
                        onChange={e => setPublishMode(e.target.value as any)}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="standard_post">Standard Post</option>
                        <option value="hierarchical_page">Page (Hierarchical)</option>
                        <option value="acf_post">ACF Custom Post</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                      <select
                        value={postStatus}
                        onChange={e => { setPostStatus(e.target.value); if (e.target.value !== 'future') setScheduledDate(''); }}
                        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="draft">Draft</option>
                        <option value="publish">Publish</option>
                        <option value="pending">Pending Review</option>
                        <option value="future">Schedule</option>
                      </select>
                    </div>
                    {postStatus === 'future' && (
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Schedule Date & Time</label>
                        <input
                          type="datetime-local"
                          value={scheduledDate}
                          onChange={e => setScheduledDate(e.target.value)}
                          min={new Date().toISOString().slice(0, 16)}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    )}
                    {publishMode === 'acf_post' && (
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">CPT</label>
                        <select
                          value={cptSlug}
                          onChange={e => setCptSlug(e.target.value)}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">Select CPT...</option>
                          {cpts.map(c => (
                            <option key={c.slug} value={c.slug}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <button
                      onClick={handlePublish}
                      disabled={publishing || wpMissing || (publishMode === 'acf_post' && !cptSlug) || (postStatus === 'future' && !scheduledDate)}
                      className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      title={wpMissing ? 'Configure WordPress credentials first' : ''}
                    >
                      {publishing && <Spinner />}
                      {postStatus === 'future' ? 'Schedule' : selectedArticle.status === 'published' ? 'Republish' : 'Publish'}
                    </button>
                  </div>

                  {gdriveAvailable && (
                    <label className="flex items-start gap-2 p-2 bg-white border border-gray-200 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useGdriveImages}
                        onChange={e => setUseGdriveImages(e.target.checked)}
                        className="h-4 w-4 mt-0.5"
                      />
                      <div className="text-xs">
                        <div className="font-medium text-gray-700">Host body images on Google Drive (lh3 links)</div>
                        <div className="text-gray-500">Skip WP Media for body images; use Drive's public CDN.</div>
                      </div>
                    </label>
                  )}
                </div>
              )}

              {/* Scheduled info (WP future-publish) */}
              {selectedArticle.status === 'scheduled' && (
                <div className="px-6 py-4 border-t border-gray-200 bg-teal-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-teal-800 font-medium text-sm">Scheduled on WordPress</span>
                      {selectedArticle.wp_url && (
                        <a href={selectedArticle.wp_url} target="_blank" rel="noreferrer" className="text-teal-700 underline text-sm ml-2">
                          View draft on WP
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {selectedArticle.wp_scheduled_date && (
                      <p className="text-teal-700 text-xs">
                        Will publish: <span className="font-medium">
                          {new Date(selectedArticle.wp_scheduled_date).toLocaleString()}
                        </span>
                      </p>
                    )}
                    <p className="text-teal-700 text-xs">
                      {selectedArticle.wp_post_id && <span>WP Post ID: {selectedArticle.wp_post_id}</span>}
                      {selectedArticle.publish_mode && (
                        <span className="ml-2 uppercase text-[10px] tracking-wider">
                          · {selectedArticle.publish_mode.replace('_', ' ')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Published info */}
              {selectedArticle.status === 'published' && (
                <div className="px-6 py-4 border-t border-gray-200 bg-green-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-green-800 font-medium text-sm">Published</span>
                      {selectedArticle.wp_url && (
                        <a href={selectedArticle.wp_url} target="_blank" rel="noreferrer" className="text-green-700 underline text-sm ml-2">
                          View on WordPress
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleUnpublish}
                        className="px-3 py-1 bg-amber-100 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-200"
                      >
                        Unpublish (Allow Republish)
                      </button>
                    </div>
                  </div>
                  {selectedArticle.published_at && (
                    <p className="text-green-700 text-xs mt-1">
                      Published: {new Date(selectedArticle.published_at).toLocaleString()}
                      {selectedArticle.wp_post_id && <span className="ml-2">• WP Post ID: {selectedArticle.wp_post_id}</span>}
                      {selectedArticle.publish_mode && (
                        <span className="ml-2 uppercase text-[10px] tracking-wider">
                          · {selectedArticle.publish_mode.replace('_', ' ')}
                        </span>
                      )}
                    </p>
                  )}
                </div>
              )}

              {/* Edit History */}
              {selectedArticle.edit_history && selectedArticle.edit_history.length > 0 && (
                <div className="px-6 py-3 border-t border-gray-200 bg-white">
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">Edit History</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedArticle.edit_history.slice().reverse().map((entry, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] text-gray-500">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          entry.action === 'created' ? 'bg-blue-400' :
                          entry.action === 'edited' ? 'bg-amber-400' :
                          entry.action === 'published' ? 'bg-green-400' : 'bg-gray-400'
                        }`} />
                        <span className="font-medium text-gray-700">{entry.action}</span>
                        {entry.user && <span>by {entry.user}</span>}
                        <span className="text-gray-400">{new Date(entry.timestamp).toLocaleString()}</span>
                        {entry.details && <span className="text-gray-400">— {entry.details}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Activity Log Panel (30%) ── */}
        <div className="w-80 flex-shrink-0">
          <div className="sticky top-24">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Activity Log</h3>
              <button
                onClick={() => loadArticles()}
                className="px-2 py-0.5 text-[11px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                title="Refresh logs"
              >
                ↻ Refresh
              </button>
              <button
                onClick={() => {
                  setArticles(prev => prev.map(a => ({ ...a, pipeline_logs: [] })));
                }}
                className="px-2 py-0.5 text-[11px] bg-red-100 hover:bg-red-200 text-red-600 rounded transition-colors"
                title="Clear logs"
              >
                ✕ Clear
              </button>
            </div>
            <div className="bg-gray-900 rounded-lg p-3 h-[calc(100vh-180px)] overflow-y-auto font-mono text-xs text-green-400 space-y-0.5">
              {allLogs.length === 0 ? (
                <span className="text-gray-500">Waiting for pipeline to start...</span>
              ) : (
                allLogs.map((log, i) => (
                  <div key={i} className={`${
                    log.startsWith('---') ? 'text-cyan-400 font-semibold mt-2 mb-1' :
                    log.includes('ERROR') ? 'text-red-400' :
                    log.includes('complete') || log.includes('Complete') ? 'text-green-300 font-semibold' :
                    log.includes('Starting') || log.includes('Running') || log.includes('Generating') || log.includes('Writing') || log.includes('Fetching') ? 'text-yellow-400' :
                    ''
                  }`}>
                    {log}
                  </div>
                ))
              )}
              <div ref={logsEndRef} />
            </div>
          </div>
        </div>
      </div>

      <ContentEditorModal
        isOpen={editorOpen}
        onClose={handleEditorClose}
        onSave={handleEditorClose}
        onRewrite={editorArticleId ? () => handleRewrite(editorArticleId) : undefined}
        contentType="article"
        contentId={editorArticleId || undefined}
        contentTitle={editorTitle}
        apiEndpoint={editorArticleId ? `/content-writer/articles/${editorArticleId}/content` : undefined}
      />

      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        contentType="content_writer"
        contentId={shareArticleId}
        contentTitle={shareArticleTitle}
      />

      {/* ═══════════ SEND TO CALENDAR MODAL ═══════════ */}
      {calendarModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Add to Content Calendar</h3>
            <p className="text-xs text-gray-500 mb-4 truncate">{calendarArticleLabel}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date *</label>
                <input
                  type="date"
                  value={calendarDate}
                  onChange={e => setCalendarDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={calendarPriority}
                  onChange={e => setCalendarPriority(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={calendarCategory}
                  onChange={e => setCalendarCategory(e.target.value)}
                  placeholder="e.g. Addiction, Recovery, Therapy"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={calendarNotes}
                  onChange={e => setCalendarNotes(e.target.value)}
                  rows={3}
                  placeholder="Optional notes about this scheduled piece..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setCalendarModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                disabled={calendarSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleSendToCalendar}
                disabled={calendarSaving || !calendarDate}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {calendarSaving && <Spinner />}
                Add to Calendar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ BULK SEND TO CALENDAR MODAL ═══════════ */}
      {bulkCalModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Send {bulkSelectedIds.size} Article{bulkSelectedIds.size > 1 ? 's' : ''} to Calendar</h3>
            <p className="text-xs text-gray-500 mb-4">Distribute selected articles evenly across a date range. Articles already on the calendar will be skipped.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={bulkCalStart}
                    onChange={e => setBulkCalStart(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date (optional)</label>
                  <input
                    type="date"
                    value={bulkCalEnd}
                    onChange={e => setBulkCalEnd(e.target.value)}
                    min={bulkCalStart}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={bulkCalSkipWeekends}
                  onChange={e => setBulkCalSkipWeekends(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded border-gray-300"
                />
                Skip weekends
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority (applied to all)</label>
                <select
                  value={bulkCalPriority}
                  onChange={e => setBulkCalPriority(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category (applied to all)</label>
                <input
                  type="text"
                  value={bulkCalCategory}
                  onChange={e => setBulkCalCategory(e.target.value)}
                  placeholder="e.g. Recovery, Therapy"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (applied to all)</label>
                <textarea
                  value={bulkCalNotes}
                  onChange={e => setBulkCalNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setBulkCalModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
                disabled={bulkCalSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkSendToCalendar}
                disabled={bulkCalSaving || !bulkCalStart}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {bulkCalSaving && <Spinner />}
                Send {bulkSelectedIds.size} to Calendar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ BLANK ARTICLE MODAL ═══════════ */}
      {blankModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Blank Article</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={blankTitle}
                  onChange={e => setBlankTitle(e.target.value)}
                  placeholder="e.g. Benefits of Outpatient Rehab"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Keyword <span className="text-gray-400 font-normal">(defaults to title)</span>
                </label>
                <input
                  type="text"
                  value={blankKeyword}
                  onChange={e => setBlankKeyword(e.target.value)}
                  placeholder={blankTitle || 'e.g. outpatient rehab benefits'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setBlankModal(false); setBlankTitle(''); setBlankKeyword(''); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={createBlankArticle}
                disabled={creatingBlank || !blankTitle.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {creatingBlank && <Spinner />}
                Create & Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
